/**
 * EventsDateFilterSheet Section Component
 *
 * Mobile bottom sheet date filter for the events listing (GRAIN-2340 follow-up):
 * mirrors SalespersonFilterSheet's drag handle / header / draft-then-Confirm
 * footer structure and tokens, but the body is the same single-date DayPicker
 * the desktop CalendarDatePicker renders in its popover. Reuses the shared
 * classNames and month/year dropdowns exported from calendar-date-picker.tsx
 * (the same reuse DateRangePicker relies on) instead of forking the calendar
 * styling. Selection is staged locally (draftDate) and only applied to the
 * parent when Confirm is pressed, matching SalespersonFilterSheet. The
 * Clear/Today pills share the desktop footer's `date_picker.*` copy.
 */

// React
import { useCallback, useEffect, useMemo, useState } from 'react';

// Libraries
import { DayPicker } from 'react-day-picker';
import { setMonth, setYear } from 'date-fns';

// Shared components
import { cn } from '@/lib/utils';
import { Sheet, SheetClose, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { CalendarPickerDropdown, calendarClassNames, MONTHS, FORMATTERS } from '@ui/calendar-date-picker';
import { CloseIcon } from '@components/icons';
import { MobilePillButton, MobilePrimaryButton } from '../../layouts/MobileButtons';

// Hooks
import { useTranslation } from '@hooks/useTranslation';

// Types
import type { EventsDateFilterSheetProps } from '../types';

export type { EventsDateFilterSheetProps };

// Matches the desktop CalendarDatePicker's month/year dropdown bounds
// (calendar-date-picker.tsx defaults) so both pickers offer the same range.
const CALENDAR_MIN_YEAR = 2020;
const CALENDAR_MAX_YEAR = 2030;

export function EventsDateFilterSheet({ open, onOpenChange, selected, onSelect }: EventsDateFilterSheetProps) {
  const { t } = useTranslation();
  const [month, setDisplayMonth] = useState<Date>(selected ?? new Date());
  // Draft selection — day picks and Today only mutate this local state.
  // Nothing is applied to the parent until Confirm. Resynced to the applied
  // selection each time the sheet opens, so dismissing without confirming
  // (close button / backdrop) discards the edit.
  const [draftDate, setDraftDate] = useState<Date | undefined>(selected);

  // Re-anchor the visible month and the draft each time the sheet opens — the
  // component stays mounted while closed, so without this a reopen would show
  // whichever month/draft was last browsed even after the selection changed
  // or was cleared.
  useEffect(() => {
    if (open) {
      setDisplayMonth(selected ?? new Date());
      setDraftDate(selected);
    }
  }, [open, selected]);

  const handleMonthChange = useCallback((val: string) => {
    setDisplayMonth((prev) => setMonth(prev, parseInt(val)));
  }, []);

  const handleYearChange = useCallback((val: string) => {
    setDisplayMonth((prev) => setYear(prev, parseInt(val)));
  }, []);

  const monthItems = useMemo(
    () => MONTHS.map((name, i) => ({ label: name, value: String(i) })),
    [],
  );

  const yearItems = useMemo(
    () =>
      Array.from({ length: CALENDAR_MAX_YEAR - CALENDAR_MIN_YEAR + 1 }, (_, i) => ({
        label: String(CALENDAR_MIN_YEAR + i),
        value: String(CALENDAR_MIN_YEAR + i),
      })),
    [],
  );

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        hideClose
        onOpenAutoFocus={(event) => event.preventDefault()}
        className={cn(
          'flex max-h-sheet-mobile flex-col rounded-t-input border-dinnerplate-white bg-off-white p-0 pb-safe-bottom',
        )}
      >
        <div className="mx-auto mt-2 h-1 w-12 shrink-0 rounded-full bg-grey-light" />
        <SheetHeader className="shrink-0 flex-row items-center justify-between space-y-0 border-b border-dinnerplate-white p-6 text-left">
          <SheetTitle className="font-inter text-sm font-semibold leading-normal text-off-black">
            {t('toolbar.select_date')}
          </SheetTitle>
          <SheetDescription className="sr-only">
            {t('toolbar.select_date')}
          </SheetDescription>
          <SheetClose
            className="shrink-0 rounded-xs text-off-black focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-off-black/30"
            aria-label="Close"
          >
            <CloseIcon className="size-4" />
          </SheetClose>
        </SheetHeader>

        <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto px-6 py-6 [&::-webkit-scrollbar]:hidden [scrollbar-width:none]">
          <div className="flex justify-center gap-3">
            <CalendarPickerDropdown
              value={String(month.getMonth())}
              items={monthItems}
              onSelect={handleMonthChange}
              className="flex-1 h-[45px]"
            />
            <CalendarPickerDropdown
              value={String(month.getFullYear())}
              items={yearItems}
              onSelect={handleYearChange}
              className="flex-1 h-[45px]"
            />
          </div>

          <DayPicker
            mode="single"
            selected={draftDate}
            onSelect={(date) => setDraftDate(date)}
            month={month}
            onMonthChange={setDisplayMonth}
            showOutsideDays
            hideNavigation
            weekStartsOn={0}
            formatters={FORMATTERS}
            classNames={calendarClassNames}
          />
        </div>

        <div className={cn('flex shrink-0 border-t border-dinnerplate-white px-6 py-4', draftDate ? 'justify-between' : 'justify-end')}>
          {draftDate && (
            <MobilePillButton
              onClick={() => setDraftDate(undefined)}
              aria-label={t('date_picker.clear_aria_label')}
              className="px-3 py-2 text-nav"
            >
              {t('date_picker.clear')}
            </MobilePillButton>
          )}
          <MobilePillButton
            onClick={() => {
              const today = new Date();
              setDraftDate(today);
              setDisplayMonth(today);
            }}
            aria-label={t('date_picker.today_aria_label')}
            className="px-3 py-2 text-nav"
          >
            {t('date_picker.today')}
          </MobilePillButton>
        </div>

        <div className="shrink-0 border-t border-dinnerplate-white p-6">
          <MobilePrimaryButton
            onClick={() => {
              onSelect(draftDate);
              onOpenChange(false);
            }}
            className="w-full justify-center text-nav"
          >
            {t('toolbar.confirm')}
          </MobilePrimaryButton>
        </div>
      </SheetContent>
    </Sheet>
  );
}
